/**
 * Single source of truth for the V2 wave rollout (R22). Nav dimming,
 * route gating and interstitials all read this registry. Flip a surface
 * to 'live' when its wave lands:
 *   U7 home → live · U8 mail (+inbox merge) → live · U10 hermes →
 *   live · U11 projects/plans/collab → live · U12 status/workshop/
 *   preview/audits → live (interstitial count reaches zero).
 *
 * Consolidation (feat/subnav-office-tovos): the AI surfaces collapse to
 * two nav entries — Office and TovOS. Studio/Design/Audits fold into
 * Office; herdr/Workshop/Plans fold into TovOS; Collab and Hermes are
 * retired outright. Surfaces still referenced by a Gate route but no
 * longer wanted in the sidebar carry `hidden: true` (kept for gating,
 * dropped from the rails); surfaces with no Gate reference are removed
 * entirely and their old paths redirect (see routes.tsx).
 */
/**
 * Bare embed surface (R-embed): `/agenda?embed=1` (and any route) renders
 * ONLY the routed page — no rail, appbar, bottom-nav, ambient or overlay
 * chrome — so it can drop cleanly into a phone-PWA iframe. Detected ONCE
 * from the initial URL (a module constant, not per-render): in-page nav on
 * these surfaces switches views via state, never a router navigation that
 * would drop the query, so reading it once is both correct and stable.
 * Embed requests come from the operator-gated origin, so an embedded live
 * surface stays authorized exactly like the same surface in the full shell
 * — embed is a chrome switch, never an auth wall.
 */
export const IS_EMBED =
  typeof window !== 'undefined' &&
  new URLSearchParams(window.location.search).get('embed') === '1'

export type SurfaceStatus = 'live' | 'building' | 'upcoming'

export interface SurfaceDef {
  id: string
  label: string
  navTo: string
  status: SurfaceStatus
  /** Where this surface lives in V1, when different from the V2 path. */
  v1Path?: string
  /** Kept for route gating / crumb resolution but omitted from the nav rails. */
  hidden?: boolean
  /** Rendered as a plain <a> (full page load) — navTo lives OUTSIDE the SPA
   *  (Caddy-routed app on the same domain, e.g. /pdfcn). */
  external?: boolean
}

export const SURFACES: SurfaceDef[] = [
  { id: 'home', label: 'Hub', navTo: '/', status: 'live' }, // U7 ✓
  { id: 'tovos', label: 'TovOS', navTo: '/tovos', status: 'live' }, // living-system command surface — Archie + herdr/Workshop/Plans folded in
  { id: 'hoxton', label: 'Hoxton', navTo: '/hoxton', status: 'live' }, // 24/7 planner/PA (ZeroClaw) — chat + agenda
  { id: 'office', label: 'Office', navTo: '/office', status: 'live' }, // operator floor — Floor/Studio/Design/Audits behind a SubNav
  { id: 'pdfcn', label: 'PDF', navTo: '/pdfcn', status: 'live' }, // archiefpagina in de shell; de upstream docs-site draait nog intern op :7457 voor pdf-studio en de MCP
  { id: 'projects', label: 'Projects', navTo: '/projects', status: 'live' }, // U11 ✓
  // 'inbox' is gone as a surface (U8): /inbox redirects into Mail.
  { id: 'mail', label: 'Mail', navTo: '/mail', status: 'live' }, // U8 ✓
  { id: 'status', label: 'Status', navTo: '/status', status: 'live' }, // U12 ✓
  { id: 'preview', label: 'Preview', navTo: '/preview', status: 'live' }, // U12 ✓
  // ── Hidden from the rails, kept for route gating (surfaced as TovOS tabs). ──
  { id: 'plans', label: 'Plans', navTo: '/plans', status: 'live', hidden: true }, // U11 ✓ — TovOS Plans tab
  { id: 'workshop', label: 'Workshop', navTo: '/workshop', status: 'live', hidden: true }, // U12 ✓ — TovOS Workshop tab
  // Retired / folded (no Gate reference): studio, design, audits → Office
  // children; agents/collab/hermes → redirect to TovOS. See routes.tsx.
]

export function getSurface(id: string): SurfaceDef {
  const s = SURFACES.find((x) => x.id === id)
  if (!s) throw new Error(`Unknown surface: ${id}`)
  return s
}

export function isLive(id: string): boolean {
  return getSurface(id).status === 'live'
}

/** Surfaces shown in the nav rails (hidden ones are gate-only). */
export const NAV_SURFACES: SurfaceDef[] = SURFACES.filter((s) => !s.hidden)

/** Longest-prefix surface match for a pathname (appbar crumb etc.). */
export function surfaceForPath(pathname: string): SurfaceDef | undefined {
  if (pathname === '/') return getSurface('home')
  return SURFACES.filter((s) => s.navTo !== '/' && pathname.startsWith(s.navTo)).sort(
    (a, b) => b.navTo.length - a.navTo.length,
  )[0]
}
